import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BuyerPreviewService } from '../buyer-preview.service';
import { MultiSectionFormPreviewService } from '../multi-section-form-preview/multi-section-form-preview.service';
import { MultiSectionFormPreviewApiService } from '../multi-section-form-preview/multi-section-form-preview-api.service';
import { FormScore, RiskFormAssignmentType, IRiskFormAssignmentRequest } from './scores-analysis.models';
import { KeyValuePipe } from '@angular/common';
import { NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-scores-analysis',
  templateUrl: './scores-analysis.component.html',
  styleUrls: ['./scores-analysis.component.css'],
  standalone: true,
  imports: [
    KeyValuePipe,
    FormsModule,
    NgStyle
  ],
})
export class ScoresAnalysisComponent {
  
  data: FormScore[] | null = null;
  supplierAdminUserList: any;
  assignToUserSelection = null;
  linkedFormAssignmentMandatory = false;
  enableSubmit = false;

  // SM-1127: Risk Form Assignment properties
  RiskFormAssignmentType = RiskFormAssignmentType; // Expose enum to template
  assignToSelection: any = null; // null = placeholder, 'self' = current user, user object, 'newUser'
  currentUserEmail = ''; // Will be set from API
  currentUserDisplayName = ''; // Full name for display
  newUserFirstName = '';
  newUserLastName = '';
  newUserEmail = '';
  emailValidationMessage = '';
  isEmailValid = true;
  isAssigning = false;

  legend = {
    'Not Applicable': 'fa fa-circle text-warning',
    Compliant: 'fa fa-circle text-success',
    'Non-Compliant': 'fa fa-circle text-danger',
  };

  // SM-3555: Section heading display labels (maps raw API keys to formatted labels)
  sectionHeadingLabels: Record<string, string> = {
    'compliance': 'Compliance',
    'cmmC_Statuses': 'CMMC_Statuses',
    'risk': 'Risk'
  };

  private _modalService = inject(NgbModal);
  private _buyerPreviewService = inject(BuyerPreviewService);
  public msfps = inject(MultiSectionFormPreviewService);
  private _msfapi = inject(MultiSectionFormPreviewApiService);

  swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary'
    },
    buttonsStyling: false,
  });

  @ViewChild('scoreAnalysis', { static: true })
  scoreAnalysisModalTemplate!: TemplateRef<any>;

  openFullscreen(content: TemplateRef<any>) {
    this._modalService.open(content, { size: 'xl', scrollable: true });
  }
  openFullscreenForMultiSection(content: TemplateRef<any>) {
    this._modalService.open(content, { fullscreen: true, scrollable: true });
  }

  asIsOrder(a: any, b: any) {
    return 1;
  }

  // SM-3555: Get display label for section heading
  getSectionHeadingLabel(key: string): string {
    return this.sectionHeadingLabels[key] || key;
  }

  getAsArray(val: any): any[] {
    return val;
  }

  shouldAssignLinkedForm(riskValue: any) {
    // Guard against an empty/absent risk array or non-string score: the camelCase
    // payload may have risk: [] (no risk row), so riskValue[0] can be undefined.
    const normalizedScore = String(riskValue?.[0]?.score ?? '').toLowerCase();
    this.linkedFormAssignmentMandatory = (this.msfps.isReviewClicked && this.msfps.formType === 'CMMC' && normalizedScore.includes('pending'));
    return this.linkedFormAssignmentMandatory;
  }

  // SM-1127: Check if form can be submitted based on assignment selection
  canSubmitForm(): boolean {
    if (!this.linkedFormAssignmentMandatory) {
      return true;
    }

    if (this.assignToSelection === null) {
      return false; // Nothing selected
    } else if (this.assignToSelection === 'self') {
      return true;
    } else if (this.assignToSelection === 'newUser') {
      return this.isNewUserFormValid();
    } else if (this.assignToSelection?.Value) {
      return true; // Existing user selected
    }
    return false;
  }

  // SM-1127: Get assignment type from dropdown selection
  getAssignmentType(): RiskFormAssignmentType {
    if (this.assignToSelection === 'self') {
      return RiskFormAssignmentType.AssignToSelf;
    } else if (this.assignToSelection === 'newUser') {
      return RiskFormAssignmentType.RequestNewUser;
    } else {
      return RiskFormAssignmentType.ExistingUser;
    }
  }

  // SM-1127: Handle dropdown selection change
  onAssignmentSelectionChange(): void {
    // Reset new user fields when not selecting new user
    if (this.assignToSelection !== 'newUser') {
      this.newUserFirstName = '';
      this.newUserLastName = '';
      this.newUserEmail = '';
      this.emailValidationMessage = '';
      this.isEmailValid = true;
    }
  }

  // SM-1127: Validate new user form fields
  isNewUserFormValid(): boolean {
    return !!(
      this.newUserFirstName?.trim() &&
      this.newUserLastName?.trim() &&
      this.newUserEmail?.trim() &&
      this.isEmailValid
    );
  }

  // SM-1127: Validate email on blur
  validateEmail(): void {
    const email = this.newUserEmail?.trim() || '';

    if (!email) {
      this.isEmailValid = false;
      this.emailValidationMessage = 'Email is required';
      return;
    }
    if (email.length > 72) {
      this.isEmailValid = false;
      this.emailValidationMessage = 'Email must not exceed 72 characters.';
      return;
    }

    const emailRegex = /^[a-zA-Z0-9\!\#\$\%\&\'\*\+\-\/\=\?\^\_\`\{\|\}\~\\]+(\.[a-zA-Z0-9\!\#\$\%\&\'\*\+\-\/\=\?\^\_\`\{\|\}\~\\]+)*@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    if (!emailRegex.test(email)) {
      this.isEmailValid = false;
      this.emailValidationMessage = 'Please enter a valid email address.';
      return;
    }

    this.newUserEmail = email;
    this.isEmailValid = true;
    this.emailValidationMessage = '';
  }

  // SM-1127: Assign risk form and submit CCA
  // SM-1704: Fixed to show confirmation modal first, then assign risk form after user confirms
  async submitWithRiskFormAssignment(): Promise<void> {
    if (!this.canSubmitForm()) {
      return;
    }

    if (this.linkedFormAssignmentMandatory) {
      const assignmentType = this.getAssignmentType();

      // Build assignment request using CCA OrgForm RefID
      const request: IRiskFormAssignmentRequest = {
        AssignmentType: assignmentType,
        OrgFormRefID: this.msfps.orgFormId || ''
      };

      // Add type-specific fields
      switch (assignmentType) {
        case RiskFormAssignmentType.ExistingUser:
          request.AssignToUserRefID = this.assignToSelection?.Value;
          request.Email = this.assignToSelection?.Email; // SM-1703: Include email for validation
          break;
        case RiskFormAssignmentType.RequestNewUser:
          request.FirstName = this.newUserFirstName.trim();
          request.LastName = this.newUserLastName.trim();
          request.Email = this.newUserEmail.trim();
          break;
      }

      // SM-1704: Store the pending assignment in the service
      // The service will process it AFTER user confirms submission
      this.msfps.pendingRiskFormAssignment = request;
      
      // Open confirmation modal - assignment will happen after user clicks OK
      this.msfps.submitConfirm();
    } else {
      // No risk form assignment required, proceed directly
      this.msfps.submitConfirm();
    }
  }

  ngAfterViewInit() {
    this._buyerPreviewService.cqScore$.subscribe({
      next: (cqScores) => {
        this.data = cqScores;
        if (this.data) {
          this.openFullscreen(this.scoreAnalysisModalTemplate);
          this.msfps.getSupplierUsers().subscribe((userList) => {
            this.processUserList(userList);
            this.resetValues();
          });
        }
      },
    });

    this.msfps.cqScore$.subscribe({
      next: (cqScores) => {
        this.data = cqScores;
        if (this.data) {
          this.openFullscreenForMultiSection(this.scoreAnalysisModalTemplate);
          this.msfps.getSupplierUsers().subscribe((userList) => {
            this.processUserList(userList);
            this.resetValues();
          });
        }
      },
    });

  }

  // SM-1127: Process user list and set current user email
  private processUserList(userList: any): void {
    const allUsers = userList.UserList || [];
    // Get current user email from localStorage or API response
    this.currentUserEmail = localStorage.getItem('UserEmail') || userList.CurrentUserEmail || '';
    
    // Find current user in the list to get their display name
    const currentUser = allUsers.find(
      (user: any) => user.Email?.toLowerCase() === this.currentUserEmail?.toLowerCase()
    );
    
    // Set display name: "FirstName LastName (email)" or just email if not found
    if (currentUser) {
      this.currentUserDisplayName = `${currentUser.Text} (${currentUser.Email})`;
    } else {
      this.currentUserDisplayName = this.currentUserEmail;
    }
    
    // Filter out current user from the list to avoid duplicate entries
    // The "(Me)" option is shown separately with the user's name
    this.supplierAdminUserList = allUsers.filter(
      (user: any) => user.Email?.toLowerCase() !== this.currentUserEmail?.toLowerCase()
    );
  }

  resetValues(): void {
    this.assignToUserSelection = null;
    this.linkedFormAssignmentMandatory = false;
    this.enableSubmit = false;
    // SM-1127: Reset assignment fields
    this.assignToSelection = null;
    this.newUserFirstName = '';
    this.newUserLastName = '';
    this.newUserEmail = '';
    this.emailValidationMessage = '';
    this.isEmailValid = true;
    this.isAssigning = false;
    // SM-1704: Clear any pending assignment in the service
    this.msfps.pendingRiskFormAssignment = null;
  }
}
